import React, { Fragment, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as actionCreators from "./actions.js";
import { duressService } from "client-app-core";
import { getEcoSettingByKey } from "orion-components/Services/EcoSettingService/selectors";
import DuressNotification from "./components/DuressNotification";

const APPBAR_ELEMENT_ID = "CB2_appBar";
const TRIPLE_CLICK_TIMEOUT = 400;

/**
 * Clears any text selection in the browser
 */
const clearTextSelection = () => {
	if (window.getSelection) {
		window.getSelection().removeAllRanges();
	} else if (document.selection) {
		document.selection.empty();
	}
};

/**
 * Sets user-select CSS properties on an element
 * @param {HTMLElement} element - The element to modify
 * @param {string} value - The user-select value ("none" or "")
 */
const setUserSelect = (element, value) => {
	if (!element) return;

	element.style.userSelect = value;
	element.style.webkitUserSelect = value;
	element.style.mozUserSelect = value;
};

const DuressService = () => {
	const dispatch = useDispatch();
	const { subscribeToDuressStatus } = actionCreators;
	const duressAlarmEnabled = useSelector(
		(state) =>
			state.session?.identity?.workstationId && (getEcoSettingByKey("duressAlarm")(state)?.enabled || false)
	);

	const clickCountRef = useRef(0);
	const clickTimerRef = useRef(null);
	const observerRef = useRef(null);
	const isListenerAttachedRef = useRef(false);
	const hasSubscribedRef = useRef(false);

	const handleTripleClick = useCallback(() => {
		clearTextSelection();

		duressService.activateDuressAlarm((err, response) => {
			if (err) {
				console.error("Error activating duress alarm:", err);
			} else {
				console.debug("Duress alarm activated");
			}
		});
	}, []);

	const resetClickState = useCallback(() => {
		clickCountRef.current = 0;
		const appBarElement = document.getElementById(APPBAR_ELEMENT_ID);
		setUserSelect(appBarElement, "");
	}, []);

	const handleAppBarClick = useCallback(
		(event) => {
			clickCountRef.current++;

			if (clickCountRef.current === 1) {
				clickTimerRef.current = setTimeout(resetClickState, TRIPLE_CLICK_TIMEOUT);
			} else if (clickCountRef.current === 2) {
				// Disable text selection on second click to prevent selection on potential third click
				const appBarElement = document.getElementById(APPBAR_ELEMENT_ID);
				setUserSelect(appBarElement, "none");
			} else if (clickCountRef.current === 3) {
				clearTimeout(clickTimerRef.current);
				clickCountRef.current = 0;
				// Re-enable text selection
				const appBarElement = document.getElementById(APPBAR_ELEMENT_ID);
				setUserSelect(appBarElement, "");
				handleTripleClick();
			}
		},
		[handleTripleClick, resetClickState]
	);

	const attachEventListener = useCallback(
		(appBarElement) => {
			if (!isListenerAttachedRef.current && appBarElement) {
				appBarElement.addEventListener("click", handleAppBarClick);
				isListenerAttachedRef.current = true;
			}
		},
		[handleAppBarClick]
	);

	const detachEventListener = useCallback(
		(appBarElement) => {
			if (isListenerAttachedRef.current && appBarElement) {
				appBarElement.removeEventListener("click", handleAppBarClick);
				isListenerAttachedRef.current = false;
			}
		},
		[handleAppBarClick]
	);

	const setupMutationObserver = useCallback(() => {
		observerRef.current = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				mutation.addedNodes.forEach((node) => {
					if (node.nodeType === Node.ELEMENT_NODE) {
						const foundElement =
							node.id === APPBAR_ELEMENT_ID ? node : node.querySelector?.(`#${APPBAR_ELEMENT_ID}`);
						if (foundElement) {
							attachEventListener(foundElement);
							observerRef.current?.disconnect();
						}
					}
				});
			});
		});

		observerRef.current.observe(document.body, {
			childList: true,
			subtree: true
		});
	}, [attachEventListener]);

	const cleanup = useCallback(() => {
		const currentAppBarElement = document.getElementById(APPBAR_ELEMENT_ID);
		if (currentAppBarElement) {
			detachEventListener(currentAppBarElement);
		}

		if (clickTimerRef.current) {
			clearTimeout(clickTimerRef.current);
		}

		if (observerRef.current) {
			observerRef.current.disconnect();
		}
	}, [detachEventListener]);

	// Subscribe to duress status on mount (only once)
	useEffect(() => {
		if (duressAlarmEnabled && !hasSubscribedRef.current) {
			dispatch(subscribeToDuressStatus());
			hasSubscribedRef.current = true;
		}
	}, [duressAlarmEnabled, dispatch, subscribeToDuressStatus]);

	// Set up AppBar click listener (only when duress alarm is enabled)
	useEffect(() => {
		if (!duressAlarmEnabled) {
			// Clean up if duress alarm gets disabled
			cleanup();
			return;
		}

		const appBarElement = document.getElementById(APPBAR_ELEMENT_ID);

		if (appBarElement) {
			attachEventListener(appBarElement);
		} else {
			setupMutationObserver();
		}

		return cleanup;
	}, [duressAlarmEnabled, attachEventListener, setupMutationObserver, cleanup]);

	return <>{duressAlarmEnabled && <DuressNotification />}</>;
};

export default DuressService;
